import React, { useEffect, useRef, useState } from 'react';
import './index.css';
import { ProgressBar } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import Axios from 'axios';

const Downloader = ({ files = [], remove }) => {
  return (
    <div className='downloader'>
      <div className='card'>
        <div className='card-header'>File Downloader</div>
        <ul className='list-group list-group-flush'>
          {files.map((file) => (
            <DownloadItem key={file.downloadId} removeFile={() => remove(file.downloadId)} {...file} />
          ))}
        </ul>
      </div>
    </div>
  );
};

const DownloadItem = ({ name, file, filename, params, removeFile }) => {
  const [downloadInfo, setDownloadInfo] = useState({
    progress: 0,
    completed: false,
    total: 0,
    loaded: 0,
    error: ''
  });
  const removeFileRef = useRef(removeFile);
  removeFileRef.current = removeFile;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let removeTimer;
    let objectUrl;
    const options = {
      onDownloadProgress: (progressEvent) => {
        if (!active) return;
        const { loaded, total } = progressEvent;
        setDownloadInfo({
          progress: total > 0 ? Math.min(100, Math.floor((loaded * 100) / total)) : 0,
          loaded,
          total: total || 0,
          completed: false,
          error: ''
        });
      }
    };

    Axios.get(file, {
      params: { data: params },
      responseType: 'blob',
      signal: controller.signal,
      timeout: 60000,
      ...options
    }).then(async function (response) {
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('json') || contentType.includes('text/html')) {
        let message = 'API ไม่ได้ส่งไฟล์ดาวน์โหลด กรุณาลองใหม่อีกครั้ง';
        if (contentType.includes('json')) {
          const payload = JSON.parse(await response.data.text());
          message = payload.Message || payload.message || message;
        }
        throw new Error(message);
      }
      if (!active) return;
      objectUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: response.headers['content-type']
        })
      );

      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      setDownloadInfo((info) => ({
        ...info,
        completed: true,
        progress: 100
      }));

      link.remove();

      removeTimer = setTimeout(() => {
        removeFileRef.current();
      }, 4000);
    }).catch(async (error) => {
      if (!active || Axios.isCancel(error)) return;
      let message = error.response
        ? `ดาวน์โหลดไม่สำเร็จ (HTTP ${error.response.status}) กรุณาลองใหม่`
        : error.code === 'ECONNABORTED'
          ? 'ดาวน์โหลดใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง'
          : error.message || 'ไม่สามารถเชื่อมต่อ API ได้ กรุณาลองใหม่';
      if (error.response?.data?.text) {
        try {
          const payload = JSON.parse(await error.response.data.text());
          message = payload.Message || payload.message || message;
        } catch {
          // Keep the HTTP error when the response is not JSON.
        }
      }
      if (active) setDownloadInfo(info => ({ ...info, completed: false, error: message }));
    });
    return () => {
      active = false;
      controller.abort();
      clearTimeout(removeTimer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, filename, params]);

  const formatBytes = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <li className='z-3 list-group-item'>
      <div className='row'>
        <div className='col-12 d-flex justify-content-between'>
          <div className='d-inline font-weight-bold text-truncate'>{name}</div>
          <div className='d-inline ml-2'>
            <small>
              {downloadInfo.loaded > 0 && (
                <>
                  <span className='text-success'>{formatBytes(downloadInfo.loaded)}</span>/
                  {formatBytes(downloadInfo.total)}
                </>
              )}

              {downloadInfo.loaded === 0 && !downloadInfo.error && <>Initializing...</>}
            </small>
          </div>
          <div className='d-inline ml-2 ml-auto'>
            {downloadInfo.completed && (
              <span className='text-success'>
                Completed <Icon icon='fluent-mdl2:completed-solid' color='#34C38F' style={{ fontSize: '18px' }} />
              </span>
            )}
          </div>
        </div>
        {downloadInfo.error && (
          <div className='col-12 mt-2 d-flex align-items-center justify-content-between'>
            <span role='alert' className='text-danger'>{downloadInfo.error}</span>
            <button type='button' className='btn btn-link btn-sm' onClick={removeFile}>ปิด</button>
          </div>
        )}
        <div className='col-12 mt-2'>
          <ProgressBar
            variant={downloadInfo.error ? 'danger' : 'success'}
            now={downloadInfo.progress}
            striped={true}
            label={`${downloadInfo.progress}%`}
          />
        </div>
      </div>
    </li>
  );
};

export default Downloader;
